import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { updateSEOMeta, injectJSONLD } from '@/components/base/SEOHead';

const testimonials = [
  {
    name: '李同学',
    role: '新手创作者',
    avatar: 'https://readdy.ai/api/search-image?query=Warm%20minimalist%20portrait%20style%20avatar%20with%20soft%20cream%20background%2C%20simple%20abstract%20face%20illustration%2C%20clean%20modern%20design%20with%20warm%20tones&width=120&height=120&seq=t-avatar-1&orientation=squarish',
    content: '作为抖ying新手，这个网站帮我少走了很多弯路。从账号注册到第一条爆款视频，每一步都有详细指导。特别是算法解析那篇文章，让我彻底理解了推荐机制。',
    rating: 5,
  },
  {
    name: '张品牌',
    role: '企业主',
    avatar: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20avatar%20portrait%20style%20with%20warm%20neutral%20background%2C%20abstract%20face%20illustration%2C%20clean%20simple%20design&width=120&height=120&seq=t-avatar-2&orientation=squarish',
    content: '我们是做本地餐饮的，看了企业号运营指南后认证了蓝V，配合直播带货攻略，三个月线上订单增长了300%。强烈推荐给所有想入局抖ying的商家。',
    rating: 5,
  },
  {
    name: '王创作者',
    role: '全职博主',
    avatar: 'https://readdy.ai/api/search-image?query=Simple%20minimalist%20avatar%20illustration%20with%20cream%20background%2C%20abstract%20portrait%20style%2C%20clean%20modern%20design%20with%20warm%20color%20palette&width=120&height=120&seq=t-avatar-3&orientation=squarish',
    content: '做了两年抖ying一直不温不火，看了这里的涨粉秘籍后重新调整内容策略，三个月涨了8万粉！数据分析和内容方法论非常实用，值得反复阅读。',
    rating: 5,
  },
];

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9qm6cnnahb7382ovc20';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    updateSEOMeta({
      title: '关于我们 - 抖ying指南 | 最专业的抖ying运营知识平台',
      description: '抖ying指南致力于为创作者和品牌主提供最全面、最专业的抖ying短视频平台运营知识。了解我们的品牌故事、用户口碑和联系方式。',
      keywords: '抖ying指南,抖ying运营平台,抖ying教程,关于我们',
      canonicalUrl: 'https://douyin-guide.com/about',
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于抖ying指南',
      description: '抖ying指南是最专业的抖ying短视频平台运营知识平台。',
      url: 'https://douyin-guide.com/about',
    }, 'schema-about');
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');
    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="text-center mb-16">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-5">
              关于抖ying指南
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              抖ying指南是专注于抖ying短视频平台运营的知识分享平台，
              致力于为每一位创作者和品牌主提供最全面、最实用的抖ying运营知识体系。
            </p>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    抖ying指南诞生于一群资深内容创作者的共同愿景。我们在抖ying平台深耕多年，
                    从最初的几十个粉丝到百万级账号运营，经历了无数次的试错与成长。
                    在这个过程中，我们发现很多有才华的创作者因为不了解平台规则和运营方法而迟迟无法突破。
                  </p>
                  <p>
                    于是，我们决定将多年积累的实战经验系统化整理，打造一个专业、全面、
                    易懂的抖ying运营知识平台。无论是刚接触抖ying的新手，还是寻求突破的资深创作者，
                    都能在这里找到有价值的指导。
                  </p>
                  <p>
                    目前，抖ying指南已收录<strong>超过77篇深度原创文章</strong>，
                    覆盖新手入门、内容创作、运营技巧、流量变现、算法解析和企业号运营六大核心领域。
                    我们的内容累计被阅读<strong>超过50万次</strong>，获得了<strong>98%的读者好评率</strong>。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20warm%20coral%20orange%20and%20cream%20color%20scheme%2C%20clean%20desk%20with%20laptop%20and%20notebook%2C%20creative%20studio%20atmosphere%2C%20soft%20natural%20lighting%2C%20editorial%20quality%2C%20warm%20and%20inviting%20professional%20environment&width=1000&height=700&seq=about-brand-story&orientation=landscape"
                  alt="抖ying指南团队工作场景"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-16 bg-background-100 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-focus-2-line',
                  title: '我们的使命',
                  desc: '让每一位创作者都能获得专业的抖ying运营知识，降低学习门槛，加速成长进程。',
                },
                {
                  icon: 'ri-eye-line',
                  title: '我们的愿景',
                  desc: '成为中文互联网上最权威、最全面的抖ying短视频平台运营知识中心。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '我们的价值观',
                  desc: '坚持原创、实战导向、持续更新。我们只分享经过验证的有效方法和策略。',
                },
              ].map((item) => (
                <div key={item.title} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100">
                    <i className={`${item.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section id="reputation" className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                读者口碑
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                来自真实用户的评价，他们的认可是我们持续进步的动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6 flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5 flex-1">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-100">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="bg-background-100 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  如果你有任何关于抖ying运营的问题，或者对我们的内容有建议和反馈，
                  欢迎通过以下方式联系我们。我们会在24小时内回复。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</div>
                      <a href="mailto:contact@douyin-guide.com" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors no-underline">
                        contact@douyin-guide.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</div>
                      <span className="text-sm text-foreground-600">搜索「抖ying指南」关注我们</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-secondary-100">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900 mb-1">办公地址</div>
                      <span className="text-sm text-foreground-600">中国·北京市朝阳区</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100">
                      <i className="ri-check-line text-3xl text-accent-600"></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">发送成功！</h3>
                    <p className="text-sm text-foreground-600 mb-4">感谢你的留言，我们会在24小时内回复。</p>
                    <button
                      onClick={() => { setFormStatus('idle'); setFormError(''); }}
                      className="px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      发送新留言
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form="true"
                    id="about-contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入你的留言内容（不超过500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="spam-field"
                    />

                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2.5">{formError}</p>
                    )}

                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}